import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import StatusButtonGroup from "components/Common/StatusButtonGroup"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Alert, Form, FormFeedback, Label, Button, Row, Col, Card, CardBody, Input } from "reactstrap"
import { useMemo, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  useCreateAccountRtgsPayment,
  useEditAccountRtgsPayment,
} from "hooks/useAccountRtgsPayment"
import { useAccountBankAccounts, ACCOUNT_BANK_ACCOUNT_QUERY_KEY } from "hooks/useAccountBankAccount"
import { useAccounts } from "hooks/useAccountEntity"
import { useVehicleImporterBankAccounts, VEHICLE_IMPORTER_BANK_ACCOUNT_QUERY_KEY } from "hooks/useVehicleImporterBankAccount"
import AccountBankAccountAddEdit from "pages/AccountPages/AccountBankAccount/AccountBankAccountAddEdit"
import ImporterOnboardingModal from "pages/VehicleImporterPages/VehicleImporterList/ImporterOnboardingModal"
import { getCustomers } from "api/customerApi"
import { getVehicleImporters } from "api/vehicleImporterApi"
import { useRtgsBankTemplates } from "hooks/useRtgsBankTemplate"

const numberToWords = n => {
  if (!n || isNaN(n)) return ""
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"]
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"]
  const convert = num => {
    if (num < 20) return ones[num]
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? " " + ones[num % 10] : "")
    if (num < 1000) return ones[Math.floor(num / 100)] + " Hundred" + (num % 100 ? " " + convert(num % 100) : "")
    if (num < 100000) return convert(Math.floor(num / 1000)) + " Thousand" + (num % 1000 ? " " + convert(num % 1000) : "")
    if (num < 10000000) return convert(Math.floor(num / 100000)) + " Lakh" + (num % 100000 ? " " + convert(num % 100000) : "")
    return convert(Math.floor(num / 10000000)) + " Crore" + (num % 10000000 ? " " + convert(num % 10000000) : "")
  }
  return convert(parseInt(n)) + " Taka Only"
}

const AccountRtgsPaymentAddEdit = ({ modal, toggleModal, isEdit, editedData, isFetchingEdit, onSuccess }) => {
  const queryClient = useQueryClient()
  const { mutateAsync: createMutation, isPending: createLoading } = useCreateAccountRtgsPayment()
  const { mutateAsync: editMutation, isPending: editLoading } = useEditAccountRtgsPayment()

  const [showNewBankAccountModal, setShowNewBankAccountModal] = useState(false)
  const [showImporterModal, setShowImporterModal] = useState(false)
  const [importerModalPrefill, setImporterModalPrefill] = useState(null)

  const handleCloseNewBankAccountModal = () => {
    setShowNewBankAccountModal(false)
    queryClient.invalidateQueries({ queryKey: [ACCOUNT_BANK_ACCOUNT_QUERY_KEY] })
  }

  const { data: bankAccountResponse, isLoading: bankAccountLoading } = useAccountBankAccounts(
    { all: true, per_page: 200 },
    modal
  )

  const { data: accountResponse, isLoading: accountLoading } = useAccounts(
    { all: true, per_page: 200 },
    modal
  )

  const { data: customerResponse, isLoading: customerLoading } = useQuery({
    queryKey: ["customers", "rtgs-payment"],
    queryFn: () => getCustomers({ all: true, per_page: 500 }),
    enabled: modal,
  })

  const { data: importerResponse, isLoading: importerLoading } = useQuery({
    queryKey: ["vehicle-importers", "rtgs-payment"],
    queryFn: () => getVehicleImporters({ all: true, per_page: 500 }),
    enabled: modal,
  })

  const [selectedImporterId, setSelectedImporterId] = useState(null)

  const { data: bankTemplateResponse } = useRtgsBankTemplates(
    { all: true, is_active: true },
    modal
  )

  const { data: importerBankResponse, isLoading: importerBankLoading } =
    useVehicleImporterBankAccounts(
      selectedImporterId,
      { all: true, per_page: 200 },
      modal && !!selectedImporterId
    )

  const bankAccountRaw = bankAccountResponse?.data?.data || bankAccountResponse?.data || []
  const accountRaw = accountResponse?.data?.data || accountResponse?.data || []
  const customerRaw = customerResponse?.data?.data || customerResponse?.data || []
  const bankTemplatesRaw = bankTemplateResponse?.data?.data || bankTemplateResponse?.data || []
  const importerRaw = useMemo(
    () => importerResponse?.data?.data || importerResponse?.data || [],
    [importerResponse]
  )
  const importerBankRaw = useMemo(
    () => importerBankResponse?.data?.data || importerBankResponse?.data || [],
    [importerBankResponse]
  )

  const validationSchema = Yup.object({
    date: Yup.string().required("Please select a transfer date"),
    branch_name: Yup.string().required("Please enter branch name"),
    remitter_name: Yup.string().required("Please enter remitter name"),
    remitter_account_number: Yup.string().required("Please enter remitter account number"),
    remitter_mobile: Yup.string().nullable(),
    bank_name: Yup.string()
      .nullable()
      .max(255, "Bank name cannot exceed 255 characters")
      .test(
        "required-when-no-bank-account",
        "Bank name is required when no bank account is selected",
        function (value) {
          if (!this.parent?.remitter_account_id) {
            return !!value && String(value).trim() !== "";
          }
          return true;
        }
      ),
    remitter_routing_number: Yup.string()
      .nullable()
      .max(50, "Routing number cannot exceed 50 characters")
      .test(
        "required-when-no-bank-account",
        "Routing number is required when no bank account is selected",
        function (value) {
          if (!this.parent?.remitter_account_id) {
            return !!value && String(value).trim() !== "";
          }
          return true;
        }
      ),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .integer("Amount must be a whole number")
      .min(1, "Amount must be at least 1")
      .required("Please enter amount"),
    amount_in_words: Yup.string().nullable(),
    purpose: Yup.string().nullable(),
    account_id: Yup.mixed().nullable(),
    status: Yup.string().nullable(),
    beneficiary_type: Yup.string().nullable(),
    beneficiary_importer_id: Yup.mixed().nullable(),
    beneficiary_account_id: Yup.mixed().nullable(),
    beneficiary_name: Yup.string().required("Please enter beneficiary name"),
    beneficiary_bank_name: Yup.string().required("Please enter beneficiary bank name"),
    beneficiary_branch_name: Yup.string().required("Please enter beneficiary branch name"),
    beneficiary_account_name: Yup.string().required("Please enter beneficiary account name"),
    beneficiary_account_number: Yup.string().required("Please enter beneficiary account number"),
    beneficiary_routing_number: Yup.string().nullable(),
    notes: Yup.string().nullable(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      date: editedData?.date || new Date().toISOString().slice(0, 10),
      branch_name: editedData?.branch_name || "",
      remitter_account_id: editedData?.remitter_account_id || editedData?.remitter_account?.id || "",
      remitter_name: editedData?.remitter_name || "",
      remitter_account_number: editedData?.remitter_account_number || "",
      remitter_mobile: editedData?.remitter_mobile || "",
      bank_name: editedData?.remitter_account?.bank_name || "",
      remitter_routing_number: editedData?.remitter_account?.routing_number || "",
      amount: editedData?.amount ?? "",
      amount_in_words: editedData?.amount_in_words || "",
      purpose: editedData?.purpose || "",
      account_id: editedData?.account_id || editedData?.account?.id || "",
      status: editedData?.status || "pending",
      beneficiary_type: editedData?.beneficiary_type || "importer",
      beneficiary_customer_id: editedData?.beneficiary_type === "customer" ? (editedData?.beneficiary_id ?? "") : "",
      beneficiary_importer_id:
        editedData?.beneficiary_type === "importer" ? (editedData?.beneficiary_id ?? "") : "",
      beneficiary_account_id: editedData?.beneficiary_account_id || "",
      beneficiary_name: editedData?.beneficiary_name || "",
      beneficiary_bank_name: editedData?.beneficiary_bank_name || "",
      beneficiary_branch_name: editedData?.beneficiary_branch_name || "",
      beneficiary_account_name: editedData?.beneficiary_account_name || "",
      beneficiary_account_number: editedData?.beneficiary_account_number || "",
      beneficiary_routing_number: editedData?.beneficiary_routing_number || "",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: useCallback(
      async (values, { resetForm }) => {
        const beneficiaryType = values.beneficiary_type || null
        const beneficiaryId =
          beneficiaryType === "importer"
            ? values.beneficiary_importer_id || null
            : beneficiaryType === "customer"
            ? values.beneficiary_customer_id || null
            : null

        const payload = {
          date: values.date,
          branch_name: values.branch_name.trim(),
          remitter_account_id: values.remitter_account_id || null,
          remitter_name: values.remitter_name.trim(),
          remitter_account_number: values.remitter_account_number.trim(),
          remitter_mobile: values.remitter_mobile?.trim() || null,
          bank_name: values.bank_name?.trim() || null,
          remitter_routing_number: values.remitter_routing_number?.trim() || null,
          amount: Number(values.amount),
          amount_in_words: values.amount_in_words?.trim() || null,
          purpose: values.purpose?.trim() || null,
          account_id: values.account_id || null,
          status: values.status || "pending",
          beneficiary_type: beneficiaryType,
          beneficiary_id: beneficiaryId,
          beneficiary_account_id:
            beneficiaryType === "importer"
              ? values.beneficiary_account_id || null
              : null,
          beneficiary_name: values.beneficiary_name.trim(),
          beneficiary_bank_name: values.beneficiary_bank_name.trim(),
          beneficiary_branch_name: values.beneficiary_branch_name.trim(),
          beneficiary_account_name: values.beneficiary_account_name.trim(),
          beneficiary_account_number: values.beneficiary_account_number.trim(),
          beneficiary_routing_number: values.beneficiary_routing_number?.trim() || null,
          notes: values.notes?.trim() || null,
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)

        try {
          const res = await action
          toast.success(res?.message ?? `RTGS payment ${isEdit ? "updated" : "created"} successfully.`)
          resetForm()
          toggleModal()
          onSuccess?.()
        } catch {
          // axios interceptor already shows the error toast
        }
      },
      [isEdit, editedData, editMutation, createMutation, toggleModal]
    ),
  })

  const handleRemitterAccountChange = e => {
    const id = e.target.value
    formik.setFieldValue("remitter_account_id", id)
    if (id) {
      const account = bankAccountRaw.find(a => String(a.id) === String(id))
      if (account) {
        formik.setFieldValue("remitter_name", account.account_name || "")
        formik.setFieldValue("remitter_account_number", account.account_number || "")
        if (!formik.values.branch_name) {
          formik.setFieldValue("branch_name", account.branch_name || "")
        }
        if (account.account_mobile_number) {
          formik.setFieldValue("remitter_mobile", account.account_mobile_number)
        }
        formik.setFieldValue("bank_name", account.bank_name || "")
        formik.setFieldValue(
          "remitter_routing_number",
          account.routing_number || ""
        )
      }
    } else {
      formik.setFieldValue("remitter_name", "")
      formik.setFieldValue("remitter_account_number", "")
      formik.setFieldValue("remitter_mobile", "")
      formik.setFieldValue("branch_name", "")
      formik.setFieldValue("bank_name", "")
      formik.setFieldValue("remitter_routing_number", "")
    }
  }

  const handleBeneficiaryCustomerChange = e => {
    const id = e.target.value
    formik.setFieldValue("beneficiary_customer_id", id)
    if (id) {
      const customer = customerRaw.find(c => String(c.id) === String(id))
      if (customer) {
        formik.setFieldValue("beneficiary_name", customer.name || "")
        formik.setFieldValue("beneficiary_bank_name", customer.bank_name || "")
        formik.setFieldValue("beneficiary_branch_name", customer.branch_name || "")
        formik.setFieldValue("beneficiary_account_name", customer.name || "")
      }
    }
  }

  const handleBeneficiaryImporterChange = e => {
    const id = e.target.value
    formik.setFieldValue("beneficiary_importer_id", id)
    formik.setFieldValue("beneficiary_account_id", "")
    setSelectedImporterId(id ? Number(id) : null)
    if (!id) {
      formik.setFieldValue("beneficiary_name", "")
      formik.setFieldValue("beneficiary_bank_name", "")
      formik.setFieldValue("beneficiary_branch_name", "")
      formik.setFieldValue("beneficiary_account_name", "")
      formik.setFieldValue("beneficiary_account_number", "")
      formik.setFieldValue("beneficiary_routing_number", "")
    }
  }

  const handleBeneficiaryAccountChange = e => {
    const accountId = e.target.value
    formik.setFieldValue("beneficiary_account_id", accountId)
    if (!accountId) return

    const account = importerBankRaw.find(
      a => String(a.id) === String(accountId)
    )
    if (!account) return

    const importer = importerRaw.find(
      i => String(i.id) === String(selectedImporterId)
    )

    formik.setFieldValue("beneficiary_name", importer?.name || account.account_name || "")
    formik.setFieldValue("beneficiary_bank_name", account.bank_name || "")
    formik.setFieldValue("beneficiary_branch_name", account.branch_name || "")
    formik.setFieldValue("beneficiary_account_name", account.account_name || "")
    formik.setFieldValue("beneficiary_account_number", account.account_number || "")
    formik.setFieldValue("beneficiary_routing_number", account.routing_number || "")
  }

  useEffect(() => {
    if (isEdit || !modal || bankAccountRaw.length === 0 || formik.values.remitter_account_id) {
      return
    }
    const defaultAccount =
      bankAccountRaw.find(a => a.is_default && a.is_active !== false) ||
      bankAccountRaw.find(a => a.is_active !== false)
    if (defaultAccount) {
      formik.setFieldValue("remitter_account_id", String(defaultAccount.id))
      formik.setFieldValue("remitter_name", defaultAccount.account_name || "")
      formik.setFieldValue("remitter_account_number", defaultAccount.account_number || "")
      if (!formik.values.branch_name) {
        formik.setFieldValue("branch_name", defaultAccount.branch_name || "")
      }
      if (defaultAccount.account_mobile_number) {
        formik.setFieldValue("remitter_mobile", defaultAccount.account_mobile_number)
      }
    }
  }, [bankAccountRaw, isEdit, modal])

  useEffect(() => {
    const amount = parseInt(formik.values.amount)
    if (amount && amount > 0) {
      formik.setFieldValue("amount_in_words", numberToWords(amount))
    }
  }, [formik.values.amount])

  useEffect(() => {
    if (formik.values.beneficiary_type === "importer") {
      const id = formik.values.beneficiary_importer_id
      const next = id ? Number(id) : null
      setSelectedImporterId(prev => (prev === next ? prev : next))
    } else {
      setSelectedImporterId(null)
    }
  }, [formik.values.beneficiary_type, formik.values.beneficiary_importer_id])

  useEffect(() => {
    if (!modal) {
      setSelectedImporterId(null)
    } else if (
      isEdit &&
      formik.values.beneficiary_type === "importer" &&
      formik.values.beneficiary_importer_id
    ) {
      setSelectedImporterId(Number(formik.values.beneficiary_importer_id))
    }
  }, [modal, isEdit, formik.values.beneficiary_type, formik.values.beneficiary_importer_id])

  const isSubmitDisabled = createLoading || editLoading || bankAccountLoading || accountLoading || importerLoading || importerBankLoading || (isEdit && !!isFetchingEdit)

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit RTGS Payment" : "New RTGS Payment"}
      size="xl"
    >
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>

        {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
          <Alert color="danger" className="mb-3 py-2 px-3">
            <div className="fw-semibold mb-1 small">Please fix the following errors:</div>
            <ul className="mb-0 ps-3">
              {Object.values(formik.errors).map((err, i) => (
                <li key={i} className="small">{err}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Part 1: Sender Info */}
        <Card className="border mb-3">
          <CardBody>
            <h5 className="mb-3">Part 1 — Sender Information</h5>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label htmlFor="remitter_account_id" className="mb-0">Bank Account</Label>
                    <CanView access={[ACCESS.ACCOUNT_BANK_ACCOUNT.ADD]}>
                      <Button
                        type="button"
                        color="link"
                        size="sm"
                        className="p-0 text-decoration-none"
                        onClick={() => setShowNewBankAccountModal(true)}
                        id="rtgs-add-new-bank-account"
                      >
                        + New Bank Account
                      </Button>
                    </CanView>
                  </div>
                  {bankAccountRaw.length === 0 && !bankAccountLoading ? (
                    <Alert color="warning" className="py-2 px-3 mb-2 small">
                      <i className="bx bx-info-circle me-1"></i>
                      No company bank accounts configured. You can still enter
                      sender details manually — please ask an admin to set up a
                      default company bank account so the transfer can be
                      linked.
                    </Alert>
                  ) : null}
                  <Input
                    type="select"
                    name="remitter_account_id"
                    id="remitter_account_id"
                    value={formik.values.remitter_account_id}
                    onChange={handleRemitterAccountChange}
                    disabled={bankAccountLoading}
                  >
                    <option value="">Select bank account (auto-fill)</option>
                    {bankAccountRaw.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.account_name} — {a.bank_name}
                      </option>
                    ))}
                  </Input>
                  <small className="text-muted d-block mt-1">
                    If left empty, the default company bank account is used
                    automatically.
                  </small>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="date">Transfer Date <span className="text-danger">*</span></Label>
                  <InputField
                    type="date"
                    name="date"
                    id="date"
                    value={formik.values.date}
                    onChange={formik.handleChange}
                    invalid={formik.touched.date && !!formik.errors.date}
                  />
                  {formik.touched.date && formik.errors.date && (
                    <FormFeedback type="invalid">{formik.errors.date}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="branch_name">Branch Name <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="branch_name"
                    id="branch_name"
                    placeholder="Sending branch name"
                    value={formik.values.branch_name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.branch_name && !!formik.errors.branch_name}
                  />
                  {formik.touched.branch_name && formik.errors.branch_name && (
                    <FormFeedback type="invalid">{formik.errors.branch_name}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="remitter_name">Account Name <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="remitter_name"
                    id="remitter_name"
                    placeholder="e.g. Aurobil Cars"
                    value={formik.values.remitter_name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.remitter_name && !!formik.errors.remitter_name}
                  />
                  {formik.touched.remitter_name && formik.errors.remitter_name && (
                    <FormFeedback type="invalid">{formik.errors.remitter_name}</FormFeedback>
                  )}
                  <small className="text-muted d-block mt-1">
                    Name as it appears on the bank account.
                  </small>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="remitter_account_number">Account Number <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="remitter_account_number"
                    id="remitter_account_number"
                    value={formik.values.remitter_account_number}
                    onChange={formik.handleChange}
                    invalid={formik.touched.remitter_account_number && !!formik.errors.remitter_account_number}
                  />
                  {formik.touched.remitter_account_number && formik.errors.remitter_account_number && (
                    <FormFeedback type="invalid">{formik.errors.remitter_account_number}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="remitter_mobile">Mobile Number</Label>
                  <InputField
                    type="text"
                    name="remitter_mobile"
                    id="remitter_mobile"
                    value={formik.values.remitter_mobile}
                    onChange={formik.handleChange}
                  />
                </div>
              </Col>
            </Row>
            {!formik.values.remitter_account_id ? (
              <div
                className="rounded-3 p-3 mb-3"
                style={{
                  background: "rgba(13, 110, 253, 0.04)",
                  border: "1px dashed rgba(13, 110, 253, 0.35)",
                }}
              >
                <div className="d-flex align-items-center mb-2">
                  <i className="bx bx-plus-circle text-primary me-2 fs-5"></i>
                  <h6 className="mb-0 text-primary">
                    New Company Bank Account
                  </h6>
                  <small className="text-muted ms-2">
                    (auto-saved when you submit)
                  </small>
                </div>
                <small className="text-muted d-block mb-3">
                  These details will be saved as a new reusable company bank
                  account and linked to this RTGS payment.
                </small>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label htmlFor="bank_name">Bank Name</Label>
                      {bankTemplatesRaw.length > 0 ? (
                        <Input
                          type="select"
                          name="bank_name"
                          id="bank_name"
                          value={formik.values.bank_name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.bank_name && !!formik.errors.bank_name}
                        >
                          <option value="">Select bank</option>
                          {bankTemplatesRaw.map(t => (
                            <option key={t.id} value={t.bank_name}>
                              {t.bank_name}
                            </option>
                          ))}
                        </Input>
                      ) : (
                        <InputField
                          type="text"
                          name="bank_name"
                          id="bank_name"
                          placeholder="e.g. Dutch Bangla Bank PLC"
                          value={formik.values.bank_name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.bank_name && !!formik.errors.bank_name}
                        />
                      )}
                      {formik.touched.bank_name && formik.errors.bank_name ? (
                        <FormFeedback type="invalid">
                          {formik.errors.bank_name}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label htmlFor="remitter_routing_number">Routing Number</Label>
                      <InputField
                        type="text"
                        name="remitter_routing_number"
                        id="remitter_routing_number"
                        placeholder="e.g. 090260187"
                        value={formik.values.remitter_routing_number}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.remitter_routing_number && !!formik.errors.remitter_routing_number}
                      />
                      {formik.touched.remitter_routing_number && formik.errors.remitter_routing_number ? (
                        <FormFeedback type="invalid">
                          {formik.errors.remitter_routing_number}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </div>
            ) : null}
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="purpose">Purpose / Narration</Label>
                  <InputField
                    type="text"
                    name="purpose"
                    id="purpose"
                    value={formik.values.purpose}
                    onChange={formik.handleChange}
                  />
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label htmlFor="account_id">Debit Account (Ledger)</Label>
                  <Input
                    type="select"
                    name="account_id"
                    id="account_id"
                    value={formik.values.account_id}
                    onChange={formik.handleChange}
                    disabled={accountLoading}
                  >
                    <option value="">Select account</option>
                    {accountRaw.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </Input>
                </div>
              </Col>
              <Col md={3}>
                <StatusButtonGroup
                  name="status"
                  value={formik.values.status}
                  onChange={value => formik.setFieldValue("status", value)}
                />
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Part 2: Beneficiary Info */}
        <Card className="border mb-3">
          <CardBody>
            <h5 className="mb-3">Part 2 — Beneficiary Information</h5>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="beneficiary_type">Beneficiary Type</Label>
                  <Input
                    type="select"
                    name="beneficiary_type"
                    id="beneficiary_type"
                    value={formik.values.beneficiary_type}
                    onChange={formik.handleChange}
                  >
                    <option value="importer">Importer</option>
                    <option value="other">Other</option>
                    <option value="customer">Customer</option>
                    <option value="vendor">Vendor</option>
                  </Input>
                </div>
              </Col>
              {formik.values.beneficiary_type === "customer" && (
                <Col md={8}>
                  <div className="mb-3">
                    <Label htmlFor="beneficiary_customer_id">Select Customer (auto-fill bank info)</Label>
                    <Input
                      type="select"
                      name="beneficiary_customer_id"
                      id="beneficiary_customer_id"
                      value={formik.values.beneficiary_customer_id}
                      onChange={handleBeneficiaryCustomerChange}
                      disabled={customerLoading}
                    >
                      <option value="">Select customer</option>
                      {customerRaw.map(c => (
                        <option key={c.id} value={c.id}>{c.name} — {c.mobile || ""}</option>
                      ))}
                    </Input>
                  </div>
                </Col>
              )}
              {formik.values.beneficiary_type === "importer" && (
                <>
                  <Col md={4}>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label htmlFor="beneficiary_importer_id" className="mb-0">Select Importer</Label>
                        <Button
                          type="button"
                          color="link"
                          size="sm"
                          className="p-0 text-decoration-none"
                          onClick={() => {
                            setImporterModalPrefill(null)
                            setShowImporterModal(true)
                          }}
                        >
                          + Onboard New
                        </Button>
                      </div>
                      <Input
                        type="select"
                        name="beneficiary_importer_id"
                        id="beneficiary_importer_id"
                        value={formik.values.beneficiary_importer_id}
                        onChange={handleBeneficiaryImporterChange}
                        disabled={importerLoading}
                      >
                        <option value="">Select importer</option>
                        {importerRaw.map(imp => (
                          <option key={imp.id} value={imp.id}>{imp.name}</option>
                        ))}
                      </Input>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label htmlFor="beneficiary_account_id" className="mb-0">Importer Bank Account (auto-fill)</Label>
                        {formik.values.beneficiary_importer_id && (
                          <Button
                            type="button"
                            color="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() => {
                              const imp = importerRaw.find(
                                i => String(i.id) === String(formik.values.beneficiary_importer_id)
                              )
                              setImporterModalPrefill({
                                id: Number(formik.values.beneficiary_importer_id),
                                name: imp?.name || "",
                              })
                              setShowImporterModal(true)
                            }}
                          >
                            + Add Bank Account
                          </Button>
                        )}
                      </div>
                      <Input
                        type="select"
                        name="beneficiary_account_id"
                        id="beneficiary_account_id"
                        value={formik.values.beneficiary_account_id}
                        onChange={handleBeneficiaryAccountChange}
                        disabled={!formik.values.beneficiary_importer_id || importerBankLoading}
                      >
                        <option value="">Select bank account</option>
                        {importerBankRaw.map(acc => (
                          <option key={acc.id} value={acc.id}>
                            {(acc.label ? `${acc.label} — ` : "")}
                            {acc.bank_name} ••• {String(acc.account_number || "").slice(-4)}
                            {acc.is_default ? " (default)" : ""}
                          </option>
                        ))}
                      </Input>
                    </div>
                  </Col>
                </>
              )}
            </Row>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="beneficiary_name">Beneficiary Name <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="beneficiary_name"
                    id="beneficiary_name"
                    value={formik.values.beneficiary_name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.beneficiary_name && !!formik.errors.beneficiary_name}
                  />
                  {formik.touched.beneficiary_name && formik.errors.beneficiary_name && (
                    <FormFeedback type="invalid">{formik.errors.beneficiary_name}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="beneficiary_bank_name">Bank Name <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="beneficiary_bank_name"
                    id="beneficiary_bank_name"
                    value={formik.values.beneficiary_bank_name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.beneficiary_bank_name && !!formik.errors.beneficiary_bank_name}
                  />
                  {formik.touched.beneficiary_bank_name && formik.errors.beneficiary_bank_name && (
                    <FormFeedback type="invalid">{formik.errors.beneficiary_bank_name}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="beneficiary_branch_name">Branch Name <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="beneficiary_branch_name"
                    id="beneficiary_branch_name"
                    value={formik.values.beneficiary_branch_name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.beneficiary_branch_name && !!formik.errors.beneficiary_branch_name}
                  />
                  {formik.touched.beneficiary_branch_name && formik.errors.beneficiary_branch_name && (
                    <FormFeedback type="invalid">{formik.errors.beneficiary_branch_name}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="beneficiary_account_name">Account Name <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="beneficiary_account_name"
                    id="beneficiary_account_name"
                    value={formik.values.beneficiary_account_name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.beneficiary_account_name && !!formik.errors.beneficiary_account_name}
                  />
                  {formik.touched.beneficiary_account_name && formik.errors.beneficiary_account_name && (
                    <FormFeedback type="invalid">{formik.errors.beneficiary_account_name}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="beneficiary_account_number">Account Number <span className="text-danger">*</span></Label>
                  <InputField
                    type="text"
                    name="beneficiary_account_number"
                    id="beneficiary_account_number"
                    value={formik.values.beneficiary_account_number}
                    onChange={formik.handleChange}
                    invalid={formik.touched.beneficiary_account_number && !!formik.errors.beneficiary_account_number}
                  />
                  {formik.touched.beneficiary_account_number && formik.errors.beneficiary_account_number && (
                    <FormFeedback type="invalid">{formik.errors.beneficiary_account_number}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="beneficiary_routing_number">Routing Number</Label>
                  <InputField
                    type="text"
                    name="beneficiary_routing_number"
                    id="beneficiary_routing_number"
                    value={formik.values.beneficiary_routing_number}
                    onChange={formik.handleChange}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="amount">Amount (BDT) <span className="text-danger">*</span></Label>
                  <InputField
                    type="number"
                    name="amount"
                    id="amount"
                    min="1"
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    invalid={formik.touched.amount && !!formik.errors.amount}
                  />
                  {formik.touched.amount && formik.errors.amount && (
                    <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={8}>
                <div className="mb-3">
                  <Label htmlFor="amount_in_words">Amount in Words</Label>
                  <InputField
                    type="text"
                    name="amount_in_words"
                    id="amount_in_words"
                    value={formik.values.amount_in_words}
                    onChange={formik.handleChange}
                  />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Notes */}
        <Card className="border mb-3">
          <CardBody>
            <div className="mb-3">
              <Label htmlFor="notes">Notes</Label>
              <InputField
                type="textarea"
                name="notes"
                id="notes"
                rows={2}
                value={formik.values.notes}
                onChange={formik.handleChange}
              />
            </div>
          </CardBody>
        </Card>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {isEdit ? "Save Changes" : "Create RTGS Payment"}
            {(createLoading || editLoading) ? "..." : ""}
          </Button>
        </div>
      </Form>

      <AccountBankAccountAddEdit
        modal={showNewBankAccountModal}
        toggleModal={handleCloseNewBankAccountModal}
        isEdit={false}
        editedData={null}
      />

      <ImporterOnboardingModal
        modal={showImporterModal}
        toggleModal={() => {
          setShowImporterModal(false)
          setImporterModalPrefill(null)
        }}
        prefillImporter={importerModalPrefill}
        onSuccess={(newImporter, createdBankAccount) => {
          if (importerModalPrefill) {
            // Bank account added to existing importer — invalidate bank list and auto-select
            queryClient.invalidateQueries({
              queryKey: [`${VEHICLE_IMPORTER_BANK_ACCOUNT_QUERY_KEY}-${importerModalPrefill.id}`],
            })
            if (createdBankAccount?.id) {
              formik.setFieldValue("beneficiary_account_id", String(createdBankAccount.id))
              formik.setFieldValue("beneficiary_name", newImporter?.name || createdBankAccount.account_name || "")
              formik.setFieldValue("beneficiary_bank_name", createdBankAccount.bank_name || "")
              formik.setFieldValue("beneficiary_branch_name", createdBankAccount.branch_name || "")
              formik.setFieldValue("beneficiary_account_name", createdBankAccount.account_name || "")
              formik.setFieldValue("beneficiary_account_number", createdBankAccount.account_number || "")
              formik.setFieldValue("beneficiary_routing_number", createdBankAccount.routing_number || "")
            }
          } else {
            // New importer created — invalidate importer list and auto-select
            queryClient.invalidateQueries({ queryKey: ["vehicle-importers", "rtgs-payment"] })
            if (newImporter?.id) {
              formik.setFieldValue("beneficiary_importer_id", String(newImporter.id))
              setSelectedImporterId(Number(newImporter.id))
            }
          }
          setImporterModalPrefill(null)
        }}
      />
    </ThemeModal>
  )
}

export default AccountRtgsPaymentAddEdit
